import React from 'react'
import { XStack, YStack } from 'tamagui'

import { Card } from '@newComponents/Card'
import { Icon } from '@newComponents/Icon'
import { Paragraph } from '@newComponents/Text'
import { Link } from '@newComponents/Link'

import { DocSection } from '../shared/DocSection'
import { DocScreenshot } from '../shared/DocScreenshot'

const Members = () => (
  <>
    <Card gap='$4'>
      <DocSection id='roles-and-permissions' title='Roles & Permissions'>
        <Paragraph size='$5'>
          Every Member of a Guild has one of three roles. Roles determine what a
          Member can do within the Guild and are displayed on the Members page,
          where users are grouped by role.
        </Paragraph>
        <DocScreenshot
          src='docs/guilds/guild-members-page.png'
          alt='Guild Members tab showing Members grouped by role: Organizers, Editors, Presenters, and Members sections, each with profile cards'
          caption='The Members page groups users by role — Organizers, Editors, Presenters, and Members'
        />
        <YStack gap='$3'>
          <XStack gap='$3' alignItems='flex-start'>
            <Icon name='star' size='$5' color='$color' flexShrink={0} marginTop={2} />
            <Paragraph size='$5'>
              <Paragraph size='$5' fontWeight='$7'>Organizer</Paragraph> — Full admin
              access to the Guild. Organizers can manage Guild settings, create and
              edit <Link href='/docs/events/overview'>Events</Link>, manage Members
              (promote, demote, and remove), connect Stripe for{' '}
              <Link href='/docs/guilds/monetization'>Monetization</Link>, configure{' '}
              <Link href='/docs/guilds/bluesky'>Integrations</Link>, set up{' '}
              <Link href='/docs/guilds/forms'>Membership Forms</Link>, and send{' '}
              <Link href='/docs/guilds/messaging'>Messages</Link> to the community.
              Organizers also see a Settings link on their own Member card for quick
              access.
            </Paragraph>
          </XStack>
          <XStack gap='$3' alignItems='flex-start'>
            <Icon name='edit' size='$5' color='$color' flexShrink={0} marginTop={2} />
            <Paragraph size='$5'>
              <Paragraph size='$5' fontWeight='$7'>Editor</Paragraph> — Can create
              and edit <Link href='/docs/events/overview'>Events</Link> and{' '}
              <Link href='/docs/presentations/overview'>Presentations</Link> on
              behalf of the Guild. Editors help Organizers manage content but cannot
              access Guild settings, manage Members, or connect payment providers.
              This role is ideal for co-organizers or trusted community members who
              help run Events.
            </Paragraph>
          </XStack>
          <XStack gap='$3' alignItems='flex-start'>
            <Icon name='person' size='$5' color='$color' flexShrink={0} marginTop={2} />
            <Paragraph size='$5'>
              <Paragraph size='$5' fontWeight='$7'>Member</Paragraph> — Can attend{' '}
              <Link href='/docs/events/overview'>Events</Link>, create personal{' '}
              <Link href='/docs/presentations/overview'>Presentations</Link>, and
              participate in the community. Members cannot create Events on behalf of
              the Guild or access Guild settings. This is the default role when
              someone joins a Guild.
            </Paragraph>
          </XStack>
        </YStack>
        <Paragraph size='$5'>
          Each Member card on the Members page shows the person's profile photo,
          background photo (if they have one), name, and any Guild badges they
          have earned. The groupings make it easy to see who does what in the
          community at a glance.
        </Paragraph>
      </DocSection>
    </Card>

    <Card gap='$4'>
      <DocSection id='managing-members-settings' title='Managing Members in Settings'>
        <DocScreenshot
          src='docs/guilds/guild-settings-members.png'
          alt='Guild Settings Members page showing membership form configuration and role management options'
          caption='The Members settings page with form configuration and role management'
        />
      </DocSection>
    </Card>

    <Card gap='$4'>
      <DocSection id='promoting-and-demoting' title='Promoting & Demoting Members'>
        <Paragraph size='$5'>
          Organizers can promote any Member to Editor or Organizer, and demote
          an Editor or Organizer back to Member. To change a Member's role,
          navigate to the Members page and use the role management options on
          their Member card.
        </Paragraph>
        <Paragraph size='$5'>
          An Organizer cannot demote themselves. To step down from the Organizer
          role, they must first ensure another Member has the Organizer role,
          then have that person change their role. This prevents a Guild from
          being left without an Organizer.
        </Paragraph>
        <Paragraph size='$5'>
          A Guild can have multiple Organizers. This is recommended for active
          communities so that administration duties can be shared and the Guild
          remains manageable even if one Organizer is unavailable.
        </Paragraph>
      </DocSection>
    </Card>

    <Card gap='$4'>
      <DocSection id='removing-members' title='Removing Members'>
        <Paragraph size='$5'>
          Organizers can remove any Member from the Guild. Removing a Member
          revokes their role and removes them from the Members list, but does not
          affect their attendance records at past{' '}
          <Link href='/docs/events/overview'>Events</Link>.
        </Paragraph>
        <Paragraph size='$5'>
          Members can also leave a Guild on their own at any time from the
          Guild's profile page by clicking the Leave button. This works the same
          way — their role is revoked and they are removed from the Members list.
        </Paragraph>
      </DocSection>
    </Card>

    <Card gap='$4'>
      <DocSection id='exporting-members' title='Exporting Member Data'>
        <Paragraph size='$5'>
          Organizers can export the full member list as a{' '}
          <Paragraph size='$5' fontWeight='$7'>CSV file</Paragraph> for use in
          external tools, mailing lists, or analytics. The export includes each
          Member's name, email, role, and any data collected through{' '}
          <Link href='/docs/guilds/forms'>Membership Forms</Link>.
        </Paragraph>
      </DocSection>
    </Card>

    <Card gap='$4'>
      <DocSection id='membership-forms-overview' title='Membership Forms'>
        <Paragraph size='$5'>
          By default, anyone can join a Guild by clicking the Join button. For
          communities that need more control,{' '}
          <Link href='/docs/guilds/forms'>Membership Forms</Link> let Organizers
          collect information from prospective Members and optionally require
          approval before granting access. See the{' '}
          <Link href='/docs/guilds/forms'>Membership Forms</Link> page for full
          details.
        </Paragraph>
      </DocSection>
    </Card>

    <Card gap='$4'>
      <DocSection id='event-attendees' title='Managing Event Attendees'>
        <Paragraph size='$5'>
          Guild membership is separate from Event attendance. For managing
          attendees at individual Events — including RSVPs, waitlists, and ticket
          holders — see{' '}
          <Link href='/docs/events/ticketing-overview'>Ticketing & Payments</Link>.
        </Paragraph>
      </DocSection>
    </Card>
  </>
)

export default Members
